import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, RefreshCw, CheckCircle2, CreditCard, Ticket, XCircle } from 'lucide-react';
import { adminListFlightOrders, adminTransitionFlightOrder } from '../../services/flightService';
import { getApiBaseUrl } from '../../utils/apiConfig.js';
import { airportLabel, formatTime, ORDER_STATUS, formatMoney } from './flightData';

const usePageTitle = (title) => {
  React.useEffect(() => {
    if (title) document.title = title;
  }, [title]);
};

const StatusBadge = ({ status }) => {
  const meta = ORDER_STATUS[status] || { label: status, badge: 'bg-gray-100 text-gray-600 border-gray-200' };
  return <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${meta.badge}`}>{meta.label}</span>;
};

const FlightAdminPage = () => {
  usePageTitle('机票订单管理 | 旅报报');
  const [me, setMe] = React.useState(null);
  const [meChecked, setMeChecked] = React.useState(false);
  const [orders, setOrders] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [query, setQuery] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    fetch(`${getApiBaseUrl()}/auth/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('未登录'))))
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setMeChecked(true));
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setOrders(await adminListFlightOrders({ status: statusFilter || undefined, q: query || undefined, limit: 100 }));
    } catch (e) {
      setError(e.message || '获取订单失败');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, query]);

  React.useEffect(() => {
    if (me?.role === 'admin') load();
  }, [me, load]);

  if (!meChecked) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500"><RefreshCw className="w-6 h-6 animate-spin mr-2" />加载中…</div>;
  }

  if (me?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <ShieldAlert className="w-14 h-14 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-700 font-medium">该页面仅管理员可见</p>
          <Link to="/" className="text-sky-600 hover:underline text-sm mt-2 inline-block">返回首页</Link>
        </div>
      </div>
    );
  }

  const transition = async (orderId, action, opts = {}) => {
    if (busy) return;
    let ticketNumbers = opts.ticketNumbers;
    let note = opts.note;
    if (action === 'issue') {
      const raw = window.prompt('请输入票号（多人用英文逗号分隔）:', '');
      if (raw === null) return;
      ticketNumbers = raw.split(/[,，、\s]+/).map((s) => s.trim()).filter(Boolean);
      if (!ticketNumbers.length) { window.alert('票号不能为空'); return; }
    } else if (action === 'cancel' || action === 'fail') {
      note = window.prompt('请输入原因（将展示给用户）:', '') || '';
    } else if (action === 'confirm') {
      note = window.prompt('确认备注（可留空，如确认价格）:', '') ?? '';
    }
    setBusy(orderId);
    try {
      await adminTransitionFlightOrder(orderId, { action, note, ticket_numbers: ticketNumbers || [] });
      await load();
    } catch (e) {
      window.alert(e.message || '操作失败');
    } finally {
      setBusy('');
    }
  };

  const btn = 'inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all disabled:opacity-50';

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="logo" className="w-9 h-9" />
            <div className="font-extrabold text-gray-900">机票订单管理</div>
          </div>
          <Link to="/home" className="text-sm text-gray-600 hover:text-gray-900">返回个人中心</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-3 items-center mb-5">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 border border-gray-200 rounded-xl text-sm bg-white">
            <option value="">全部状态</option>
            {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="订单号/用户名"
            className="h-10 px-3 border border-gray-200 rounded-xl text-sm bg-white w-48"
            onKeyDown={(e) => e.key === 'Enter' && load()} />
          <button onClick={load} disabled={loading}
            className="h-10 px-4 rounded-xl bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-60 inline-flex items-center gap-1">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> 查询
          </button>
          <span className="text-sm text-gray-400 ml-auto">共 {orders.length} 单</span>
        </div>

        {error && <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.order_id} className="rounded-2xl bg-white border border-gray-100 shadow-sm p-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-bold text-gray-900">{o.order_id}</span>
                  <StatusBadge status={o.status} />
                  <span className="text-gray-500">{o.username}</span>
                  <span className="text-gray-400">{airportLabel(o.search.origin)}→{airportLabel(o.search.destination)} · {o.search.depart_date}</span>
                </div>
                <span className="font-black text-sky-600">{formatMoney(o.total_price_cny, o.currency)}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {o.offer_snapshot?.segments?.[0]?.flight_number} · {o.passengers?.length} 位乘机人 ·
                联系人 {o.contact?.name} {o.contact?.phone} · 提交于 {o.created_at ? formatTime(o.created_at) : ''}
                {o.ticket_numbers?.length > 0 && <span className="text-emerald-600"> · 票号 {o.ticket_numbers.join('、')}</span>}
              </p>
              {o.remark && <p className="text-xs text-amber-600 mt-1">用户备注：{o.remark}</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                <Link to={`/flight/orders/${o.order_id}`} className="text-xs text-sky-600 hover:underline self-center mr-1">详情 →</Link>
                {o.status === 'pending' && (
                  <>
                    <button onClick={() => transition(o.order_id, 'confirm')} disabled={busy === o.order_id}
                      className={`${btn} border-sky-200 text-sky-700 hover:bg-sky-50`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> 确认价格
                    </button>
                    <button onClick={() => transition(o.order_id, 'cancel')} disabled={busy === o.order_id}
                      className={`${btn} border-gray-200 text-gray-600 hover:bg-gray-50`}>
                      <XCircle className="w-3.5 h-3.5" /> 取消
                    </button>
                  </>
                )}
                {o.status === 'confirmed' && (
                  <>
                    <button onClick={() => transition(o.order_id, 'mark_paid')} disabled={busy === o.order_id}
                      className={`${btn} border-violet-200 text-violet-700 hover:bg-violet-50`}>
                      <CreditCard className="w-3.5 h-3.5" /> 标记已收款
                    </button>
                    <button onClick={() => transition(o.order_id, 'cancel')} disabled={busy === o.order_id}
                      className={`${btn} border-gray-200 text-gray-600 hover:bg-gray-50`}>
                      <XCircle className="w-3.5 h-3.5" /> 取消
                    </button>
                  </>
                )}
                {o.status === 'paid' && (
                  <button onClick={() => transition(o.order_id, 'issue')} disabled={busy === o.order_id}
                    className={`${btn} border-emerald-200 text-emerald-700 hover:bg-emerald-50`}>
                    <Ticket className="w-3.5 h-3.5" /> 出票（填票号）
                  </button>
                )}
              </div>
            </div>
          ))}
          {!loading && orders.length === 0 && (
            <div className="text-center py-12 text-gray-400 text-sm">没有符合条件的订单</div>
          )}
        </div>
      </main>
    </div>
  );
};

export default FlightAdminPage;
